import React, { createContext, useContext, useEffect, useState } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import { isGuestMode } from '@/utils/iframeDetector'

interface AuthContextType {
  user: User | null
  session: Session | null
  loading: boolean
  isGuest: boolean
  isPasswordRecovery: boolean
  signUp: (email: string, password: string) => Promise<{ error: any }>
  signIn: (email: string, password: string) => Promise<{ error: any }>
  signOut: () => Promise<void>
  requestPasswordReset: (email: string, redirectTo: string) => Promise<{ error: any }>
  updatePassword: (newPassword: string) => Promise<{ error: any }>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [isGuest, setIsGuest] = useState(() => isGuestMode())
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false)

  useEffect(() => {
    // Re-check guest mode on mount (for iframe detection)
    const checkGuestMode = () => {
      const guest = isGuestMode()
      setIsGuest(guest)
      return guest
    }
    
    // Check immediately
    const guest = checkGuestMode()
    
    // If in guest mode, skip authentication and clear user
    if (guest) {
      console.log('Guest mode enabled - skipping authentication')
      setUser(null)
      setSession(null)
      setLoading(false)
      return
    }

    // Get initial session
    const getInitialSession = async () => {
      console.log('Getting initial session...')
      const { data: { session } } = await supabase.auth.getSession()
      console.log('Initial session:', session)
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)
    }

    getInitialSession()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state change:', event, session)
        // Fired when the user arrives from a password reset email link
        if (event === 'PASSWORD_RECOVERY') {
          setIsPasswordRecovery(true)
        }
        setSession(session)
        setUser(session?.user ?? null)
        setLoading(false)
      }
    )

    return () => subscription.unsubscribe()
  }, [isGuest])

  const signUp = async (email: string, password: string) => {
    console.log('Signing up user:', email)
    const { error } = await supabase.auth.signUp({
      email,
      password,
    })
    console.log('Sign up result:', { error })
    return { error }
  }

  const signIn = async (email: string, password: string) => {
    console.log('Signing in user:', email)
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    console.log('Sign in result:', { error })
    return { error }
  }

  const signOut = async () => {
    console.log('Signing out user')
    await supabase.auth.signOut()
  }

  const requestPasswordReset = async (email: string, redirectTo: string) => {
    console.log('Requesting password reset for:', email, 'redirectTo:', redirectTo)
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo,
    })
    console.log('Password reset request result:', { error })
    return { error }
  }

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword })
    console.log('Update password result:', { error })
    if (!error) {
      setIsPasswordRecovery(false)
    }
    return { error }
  }

  const value = {
    user,
    session,
    loading,
    isGuest,
    isPasswordRecovery,
    signUp,
    signIn,
    signOut,
    requestPasswordReset,
    updatePassword,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
